import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const ManagerRequestsPanel = ({ user }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const fetchRequests = useCallback(async () => {
        if (!user || user.role !== 'M') return;

        try {
            setLoading(true);
            const res = await api.get('/frp/manager-change-requests');
            setRequests(res.data.requests || []);
        } catch (err) {
            console.error('Failed to fetch manager requests:', err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleAccept = async (requestId) => {
        setActionLoading(requestId);
        try {
            await api.post(`/frp/manager-change-requests/${requestId}/accept`);
            alert('Request accepted! You are now the manager of this property.');
            fetchRequests();
        } catch (err) {
            console.error('Accept failed:', err);
            alert('Failed to accept: ' + (err.response?.data?.error || err.message));
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (requestId) => {
        const reason = prompt('Optional: Enter a reason for rejection');
        setActionLoading(requestId);
        try {
            await api.post(`/frp/manager-change-requests/${requestId}/reject`, { reason });
            alert('Request rejected.');
            fetchRequests();
        } catch (err) {
            console.error('Reject failed:', err);
            alert('Failed to reject: ' + (err.response?.data?.error || err.message));
        } finally {
            setActionLoading(null);
        }
    };

    if (!user || user.role !== 'M') return null;

    if (loading) {
        return (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-orange-700 text-sm">Loading pending requests...</span>
                </div>
            </div>
        );
    }

    if (requests.length === 0) return null;

    return (
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                </div>
                <h3 className="text-lg font-bold text-orange-800">
                    Pending Manager Requests ({requests.length})
                </h3>
            </div>

            <div className="space-y-3">
                {requests.map(request => (
                    <div key={request._id} className="bg-white rounded-lg p-4 border border-orange-100 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <p className="font-semibold text-gray-900">
                                    {request.property?.name || 'Unknown Property'}
                                </p>
                                <p className="text-sm text-gray-600">
                                    {request.property?.address}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    Requested by: {request.requestedBy?.name || request.requestedBy?.email}
                                </p>
                                {request.message && (
                                    <p className="text-sm text-gray-600 mt-2 italic">
                                        "{request.message}"
                                    </p>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleReject(request._id)}
                                    disabled={actionLoading === request._id}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                                >
                                    Reject
                                </button>
                                <button
                                    onClick={() => handleAccept(request._id)}
                                    disabled={actionLoading === request._id}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50"
                                >
                                    {actionLoading === request._id ? 'Processing...' : 'Accept'}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ManagerRequestsPanel;
